import { Avatar } from "@components/ui/Avatar"
import { Card, CardBody } from "@components/ui/Card"
import { cn } from "@lib/cn"
import { formatDate } from "@lib/intl"
import { memo, useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { DirectionActivityCell, DirectionDormantProfessor } from "../types"

interface Props {
  cells: DirectionActivityCell[]
  dormant: DirectionDormantProfessor[]
}

const WEEK_DAYS_ES = ["L", "M", "X", "J", "V", "S", "D"] as const

/**
 * A five-step sequential ramp built from ONE token (PRD-59).
 *
 * It used to mix `indigo-300` and `indigo-700` in with the accent tokens, so
 * three of the five steps ignored the theme and two followed it — on the dark
 * ground the ramp stopped being monotonic, which is the only property a heatmap
 * scale has to keep. Opacity over a single token is monotonic by construction
 * and themes for free; the day's count is also on every cell's `title` and
 * `aria-label`, so the scale never carries the meaning alone
 * (`accessibility.md` §5).
 */
function intensityClass(count: number, max: number) {
  if (count === 0) return "bg-inset"
  const r = max === 0 ? 0 : count / max
  if (r < 0.25) return "bg-accent/25"
  if (r < 0.5) return "bg-accent/50"
  if (r < 0.75) return "bg-accent/75"
  return "bg-accent"
}

interface DayCell {
  date: string | null
  count: number
}

export const PlatformActivityHeatmap = memo(function PlatformActivityHeatmap({
  cells,
  dormant,
}: Props) {
  const { t } = useTranslation("direction")

  const { grid, max, total, peak } = useMemo(() => {
    const max = cells.reduce((m, c) => Math.max(m, c.count), 0)
    const total = cells.reduce((s, c) => s + c.count, 0)
    const peak = cells.reduce<DirectionActivityCell | null>((best, cell) => {
      if (!best || cell.count > best.count) return cell
      return best
    }, null)

    // Lay cells into Mon-Sun rows. Pad the start so dow=Mon is column 0.
    const sorted = [...cells].sort((a, b) => (a.date < b.date ? -1 : 1))
    const first = sorted[0]
    if (!first) return { grid: [] as DayCell[][], max, total, peak }
    const firstDow = new Date(first.date).getUTCDay()
    // 0=Sun, 1=Mon ... shift so Monday is first column
    const padStart = (firstDow + 6) % 7
    const flat: DayCell[] = []
    for (let i = 0; i < padStart; i++) flat.push({ date: null, count: 0 })
    for (const c of sorted) flat.push({ date: c.date, count: c.count })
    while (flat.length % 7 !== 0) flat.push({ date: null, count: 0 })
    const rows: DayCell[][] = []
    for (let i = 0; i < flat.length; i += 7) rows.push(flat.slice(i, i + 7))
    return { grid: rows, max, total, peak }
  }, [cells])

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-ink text-sm font-semibold">{t("activity.title")}</h2>
          <span className="text-ink-subtle text-xs">{t("activity.subtitle")}</span>
        </div>

        <div className="overflow-x-auto">
          <div className="inline-grid auto-cols-[1.25rem] grid-flow-col gap-1">
            {/* Day-of-week labels column */}
            <div className="grid grid-rows-7 gap-1 pr-1">
              {WEEK_DAYS_ES.map((d) => (
                <div
                  key={d}
                  className="text-ink-subtle flex h-5 w-5 items-center justify-center text-xs font-medium"
                  aria-hidden="true"
                >
                  {d}
                </div>
              ))}
            </div>
            {grid.map((week, wIdx) => (
              <div key={wIdx} className="grid grid-rows-7 gap-1">
                {week.map((cell, dIdx) => (
                  <div
                    key={`${wIdx}-${dIdx}`}
                    className={cn(
                      "h-5 w-5 rounded-sm",
                      cell.date ? intensityClass(cell.count, max) : "bg-transparent",
                    )}
                    title={
                      cell.date
                        ? t("activity.cell.tooltip", {
                            count: cell.count,
                            date: formatDate(cell.date),
                          })
                        : undefined
                    }
                    aria-label={
                      cell.date
                        ? t("activity.cell.aria", {
                            count: cell.count,
                            date: formatDate(cell.date),
                          })
                        : undefined
                    }
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="text-ink-subtle flex flex-wrap items-center justify-between gap-2 text-xs">
          <span>
            {t("activity.total", { count: total })}
            {peak
              ? ` · ${t("activity.peak", { count: peak.count, date: formatDate(peak.date) })}`
              : ""}
          </span>
          {dormant.length > 0 && (
            <span className="bg-attention-soft text-attention-ink rounded-full px-2 py-0.5 font-medium">
              {t("activity.dormant_count", { count: dormant.length })}
            </span>
          )}
        </div>

        {dormant.length > 0 && (
          <ul className="space-y-1">
            {dormant.map((p) => (
              <li
                key={p.professor_id}
                className="text-ink-muted flex items-center gap-2 rounded-md px-1 py-1 text-xs"
              >
                <Avatar name={p.name} initials={p.initials} color={p.color} size="sm" />
                <span className="flex-1 truncate">{p.name}</span>
                <span className="text-attention-ink tabular-nums">
                  {t("activity.dormant_days_other", { days: p.days_inactive })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  )
})
